import { useState, useEffect, useCallback, useRef } from 'react';
import { useEventSubscribe } from '../events';
import { useAuth } from '../contexts/AuthContext';
import { projectService } from '../services/projectService';
export const useUserProjects = () => {
    const { isAuthenticated, user } = useAuth();
    const [projects, setProjects] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasLoaded, setHasLoaded] = useState(false);
    // Check if user should have access to projects (authenticated and not unverified)
    const shouldLoadProjects = isAuthenticated && (user === null || user === void 0 ? void 0 : user.loginMethod) !== 'unverified';
    // Use refs to track state and prevent unnecessary re-renders/loops
    const isLoadingRef = useRef(false);
    const hasLoadedRef = useRef(false);
    const lastFetchTimeRef = useRef(0);
    const debounceTimeoutRef = useRef(null);
    // A forced refresh that arrives while a load is already in flight is dropped
    // by the guard at the top of loadProjects. This remembers it so that load
    // re-runs on completion instead of leaving the caller waiting out a full poll
    // interval — the exact stall a forced refresh exists to avoid.
    const pendingRefreshRef = useRef(false);
    const loadProjectsRef = useRef(async () => { });
    const loadProjects = useCallback(async () => {
        // Prevent multiple simultaneous loads
        if (isLoadingRef.current) {
            return;
        }
        if (!shouldLoadProjects) {
            return;
        }
        try {
            isLoadingRef.current = true;
            // Only show loading indicator if this is the initial load (no data yet)
            const isInitialLoad = projects.length === 0 && !hasLoadedRef.current;
            if (isInitialLoad) {
                setIsLoading(true);
            }
            setError(null);
            const projectsResponse = await projectService.listUserProjects();
            // Log individual project details
            projectsResponse.projects.forEach((project, index) => {
            });
            // If response is empty but we already have projects, keep existing list
            if (projectsResponse.projects.length === 0 && projects.length > 0) {
            }
            else {
                setProjects(projectsResponse.projects);
            }
            hasLoadedRef.current = true;
            setHasLoaded(true);
        }
        catch (err) {
            console.error('useUserProjects: Failed to load user projects:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to load projects';
            setError(errorMessage);
        }
        finally {
            isLoadingRef.current = false;
            setIsLoading(false);
        }
        // Drain a refresh that was requested mid-flight (see pendingRefreshRef).
        if (pendingRefreshRef.current) {
            pendingRefreshRef.current = false;
            await loadProjectsRef.current();
        }
    }, [shouldLoadProjects, projects.length]); // Only depend on shouldLoadProjects and current list size
    useEffect(() => {
        loadProjectsRef.current = loadProjects;
    }, [loadProjects]);
    // Immediate load on auth or initial mount
    useEffect(() => {
        if (!shouldLoadProjects)
            return;
        if (!hasLoadedRef.current) {
            lastFetchTimeRef.current = Date.now();
            loadProjects();
        }
    }, [shouldLoadProjects, loadProjects]);
    // Adaptive background polling while authenticated (and not unverified)
    // Poll faster when projects are actively processing, slower when idle
    // Pauses polling when browser tab is hidden to save resources
    useEffect(() => {
        if (!shouldLoadProjects)
            return;
        let intervalId = null;
        const startPolling = () => {
            if (intervalId)
                return;
            // 'restoring_preview' is a server-side build too (the preview set is being
            // regenerated), so it earns the fast cadence: the badge and the project
            // panel both clear off this refetch. 'still_looking' deliberately does
            // not: its automatic re-checks have already stopped, so it changes only
            // when a person acts, and the idle cadence picks that up.
            const hasActiveProjects = projects.some(p => p.displayStatus === 'processing' || p.displayStatus === 'restoring_preview');
            const pollInterval = hasActiveProjects ? 5000 : 30000;
            intervalId = setInterval(() => {
                if (!isLoadingRef.current) {
                    lastFetchTimeRef.current = Date.now();
                    loadProjects();
                }
            }, pollInterval);
        };
        const stopPolling = () => {
            if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };
        const handleVisibilityChange = () => {
            if (document.hidden) {
                stopPolling();
            }
            else {
                if (!isLoadingRef.current) {
                    lastFetchTimeRef.current = Date.now();
                    loadProjects();
                }
                startPolling();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        if (!document.hidden) {
            startPolling();
        }
        return () => {
            stopPolling();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [shouldLoadProjects, loadProjects, projects]);
    // Reset state when user logs out or becomes unverified
    useEffect(() => {
        if (!shouldLoadProjects) {
            setProjects([]);
            setError(null);
            setHasLoaded(false);
            hasLoadedRef.current = false;
            isLoadingRef.current = false;
            pendingRefreshRef.current = false;
        }
    }, [shouldLoadProjects]);
    // Debounced refetch function that components can call explicitly
    const refetch = useCallback(async () => {
        const now = Date.now();
        const timeSinceLastFetch = now - lastFetchTimeRef.current;
        const minDebounceTime = 2000; // 2 seconds minimum between calls
        // Clear any existing timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        if (timeSinceLastFetch < minDebounceTime) {
            const remainingTime = minDebounceTime - timeSinceLastFetch;
            return new Promise((resolve) => {
                debounceTimeoutRef.current = setTimeout(async () => {
                    lastFetchTimeRef.current = Date.now();
                    await loadProjects();
                    resolve();
                }, remainingTime);
            });
        }
        else {
            lastFetchTimeRef.current = now;
            await loadProjects();
        }
    }, [loadProjects]);
    // An access change that happens outside this list (an unlock, an Atlas
    // activation) has no other way to reach it: the background poll sits at 30s
    // while nothing is processing, which reads as "the payment did nothing" until
    // a reload. Same pattern as BillingContext's 'billing:statusRefresh'.
    useEventSubscribe('projects:refreshList', () => {
        refetch();
    }, [refetch]);
    // Undebounced sibling of refetch, for the one case that cannot tolerate a
    // delay. refetch's 2s floor is measured from lastFetchTimeRef, which the
    // background poll bumps on every tick, so a debounced call can sit idle for up
    // to 2s before it even starts.
    const forceRefresh = useCallback(async () => {
        if (isLoadingRef.current) {
            pendingRefreshRef.current = true;
            return;
        }
        lastFetchTimeRef.current = Date.now();
        await loadProjects();
    }, [loadProjects]);
    // A new project reaches this list only when something asks for it. Nothing
    // did: ControlPanel swings the panel open to My Projects the instant
    // 'project:created' fires, while the idle poll sits at 30s (a brand-new
    // project isn't in the list yet, so hasActiveProjects can't see it and the 5s
    // processing cadence doesn't kick in either) — so the user watched an empty
    // list for up to half a minute. The server commits the row, stamps the demo
    // window and flips processing_status synchronously before responding, so the
    // project is fully listable the moment this fires.
    useEventSubscribe('project:created', () => {
        forceRefresh();
    }, [forceRefresh]);
    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);
    return {
        projects,
        isLoading,
        error,
        refetch,
        hasLoaded
    };
};
